import { createContext, useContext, useMemo } from "react";

import { CreateUIAnalyticsEvent } from "@atlaskit/analytics-next";

import { useSearchParameters } from "@/components/signup/hooks/use-search-parameters";
import { AvailableProductsResponse } from "@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types";

import { ExperimentEnrollment } from "./use-experiment";
import {
    UserSegmentationStatusContext,
    useUserSegmentationStatus
} from "./use-user-segmentation-status";

export type ConfirmationPageContext = {
    availableProducts?: AvailableProductsResponse;
    userSegmentationContext: UserSegmentationStatusContext;
    experiments: Map<string, ExperimentEnrollment>;
};
const ConfirmationPageContext = createContext<ConfirmationPageContext>({
    availableProducts: undefined,
    userSegmentationContext: {
        closeUserSegmentationUI: () =>
            console.warn("closeUserSegmentationUI is not defined")
    } as UserSegmentationStatusContext,
    experiments: new Map<string, ExperimentEnrollment>()
});

export const useConfirmationPage = (): ConfirmationPageContext => {
    const context = useContext(ConfirmationPageContext);

    if (context === undefined) {
        throw new Error(
            "`useConfirmationPage` must be used within a `ConfirmationPageContext.Provider`"
        );
    }
    return context;
};

export const ConfirmationPageProvider = ({
    availableProducts,
    children
}: ConfirmationPageProviderProps) => {
    const { products } = useSearchParameters();
    const userSegmentationContext = useUserSegmentationStatus(
        products,
        availableProducts ?? null
    );

    const experiments = useMemo(() => new Map(), []);

    return (
        <ConfirmationPageContext.Provider
            value={{
                userSegmentationContext,
                availableProducts,
                experiments
            }}
        >
            {children}
        </ConfirmationPageContext.Provider>
    );
};

export type ConfirmationPageProviderProps = {
    createAnalyticsEvent: CreateUIAnalyticsEvent;
    availableProducts?: AvailableProductsResponse;
} & React.PropsWithChildren;
